// @flow
/* eslint-disable ta-code-conventions/has-matching-test,ta-code-conventions/has-matching-storydoc */

import * as React from 'react';

import { Visitor } from '@ta/lithium.context';
import type { LoginFlowOrigin } from '@ta/login.types';
import useSocialLoginGate from './useSocialLoginGate';

type Props = {|
  /** Product ID */
  pid?: number,

  /**
   * Optional. Whether we should use the join or login flow
   */
  flowOrigin?: LoginFlowOrigin,

  /**
   * Optional. Defaults to 'CORE_COMBINED'. If you want a different flow, you can use any of the values defined in com.TripResearch.membership.registration.RegistrationFlowId
   */
  flow?: string,

  /**
   * Optional callback for when the user closes the RegOverlay in AuthGated.
   */
  onClose?: () => void,

  /**
   * Optional callback for when the user successfully logs in.
   * This is called after login but before the confirm username flow (if shown).
   */
  onLoginSuccess?: () => void,

  /**
   * Optional callback for when the login flow starts.
   * This is called when login overlay shows up.
   */
  onLoginStarted?: () => void,

  /**
   * extraRegParams to be passed to RegOverlay.
   */
  extraRegParams?: Object,

  /** Render prop that provides the gated function. Put it in an onClick or whatever.
   * Use it like this: authGate().then(myFunctionToPerform)
   * */
  children: (
    loginGate: (deferredPID?: number) => Promise<void>,
    loggedInUserId: string,
  ) => React.Node,

  /**
   * Optional. Control whether force login even if logged.
   */
  forceLogin?: boolean,
|};

/**
 * Login gate for social actions.
 * Makes sure the user is logged in, and has accepted the new Terms of Service and confirmed their new username before taking the action.
 * All social actions need to be blocked using this component.
 * Disables the action in read-only mode
 *
 * ---------- IMPORTANT NOTE ----------
 * The user of this component has to make sure the transition from logged-out state to logged-in state does not cause this component to unmount/remount.
 * Otherwise the callback might get lost.
 *
 * @author amoosavi, ekarpov
 * @since Sep 2018
 */
const SocialLoginGate = (props: Props) => {
  const { userId } = React.useContext(Visitor);
  const gate = useSocialLoginGate({
    pid: props.pid,
    flow: props.flow,
    flowOrigin: props.flowOrigin,
    forceLogin: props.forceLogin,
    extraQueryParams: props.extraRegParams,
    onLogin: props.onLoginStarted,
    onSuccess: props.onLoginSuccess,
    onClose: props.onClose,
  });
  return props.children(gate, userId || '');
};

export default SocialLoginGate;
